import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fiscaliteService } from '../../api/services';
import { ControleTva, EcheanceFiscale, EtatRetenues } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Contrôle fiscal (TF-039 / TF-040) : confronte la TVA des documents à la TVA comptabilisée,
 * liste les incohérences de ligne, et affiche le calendrier des prochaines échéances fiscales.
 */
export function ControleFiscal() {
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [controle, setControle] = useState<ControleTva | null>(null);
  const [calendrier, setCalendrier] = useState<EcheanceFiscale[]>([]);
  const [retenues, setRetenues] = useState<EtatRetenues | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      fiscaliteService.getControleTva(mois, annee).then(setControle),
      fiscaliteService.getCalendrier(6).then(setCalendrier),
      fiscaliteService.getRetenues(annee).then(setRetenues),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mois, annee]);

  if (loading && !controle) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Contrôle fiscal</h1>
          <p className="page-subtitle">Cohérence TVA avant déclaration + calendrier des échéances</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-field" value={mois} onChange={(e) => setMois(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{frMois(m)}</option>
            ))}
          </select>
          <select className="input-field" value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
            {[annee + 1, annee, annee - 1, annee - 2].filter((v, i, a) => a.indexOf(v) === i)
              .sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {controle && (
        <>
          <div className={`card border-l-4 ${controle.coherent ? 'border-green-500' : 'border-red-500'}`}>
            <p className="font-semibold">
              {controle.coherent
                ? '✓ TVA cohérente — documents et comptabilité concordent'
                : '⚠ Incohérences détectées — à corriger avant la déclaration'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TvaCard title="TVA collectée"
              docs={controle.tvaCollecteeDocuments} compta={controle.tvaCollecteeComptabilisee}
              ecart={controle.ecartCollectee} />
            <TvaCard title="TVA déductible"
              docs={controle.tvaDeductibleDocuments} compta={controle.tvaDeductibleComptabilisee}
              ecart={controle.ecartDeductible} />
            <div className="card">
              <p className="text-xs text-gray-500">TVA nette à payer (documents)</p>
              <MoneyDisplay amount={controle.tvaNetteAPayer} size="lg" className="font-bold mt-1" />
              <p className="text-xs text-gray-400 mt-2">Collectée − déductible</p>
            </div>
          </div>

          <div className="card">
            <h2 className="font-semibold mb-2">Incohérences de ligne (base × taux ≠ TVA)</h2>
            {controle.anomaliesLignes.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucune incohérence de calcul détectée.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                      <th className="px-3 py-2">Document</th>
                      <th className="px-3 py-2">Désignation</th>
                      <th className="px-3 py-2 text-right">Base HT</th>
                      <th className="px-3 py-2">Taux</th>
                      <th className="px-3 py-2 text-right">TVA constatée</th>
                      <th className="px-3 py-2 text-right">TVA attendue</th>
                      <th className="px-3 py-2 text-right">Écart</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {controle.anomaliesLignes.map((a, i) => (
                      <tr key={`${a.documentId}-${i}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{a.numero}</td>
                        <td className="px-3 py-2 text-gray-600">{a.designation}</td>
                        <td className="px-3 py-2 text-right"><MoneyDisplay amount={a.baseHT} size="sm" /></td>
                        <td className="px-3 py-2">{a.taux}</td>
                        <td className="px-3 py-2 text-right"><MoneyDisplay amount={a.tvaConstatee} size="sm" /></td>
                        <td className="px-3 py-2 text-right"><MoneyDisplay amount={a.tvaAttendue} size="sm" /></td>
                        <td className="px-3 py-2 text-right text-red-600">
                          <MoneyDisplay amount={a.ecart} size="sm" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link to={`/documents/view/${a.documentId}`} className="text-blue-600 hover:underline">Voir →</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {retenues && retenues.lignes.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Retenues à la source — {retenues.annee}</h2>
            <span className="text-sm text-gray-500">Total : <MoneyDisplay amount={retenues.total} size="sm" className="font-semibold" /></span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Matricule fiscal</th>
                  <th className="px-3 py-2 text-right">Factures</th>
                  <th className="px-3 py-2 text-right">Retenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {retenues.lignes.map((r) => (
                  <tr key={r.clientId} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{r.clientNom}</td>
                    <td className="px-3 py-2 text-gray-500">{r.matriculeFiscal ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{r.nbFactures}</td>
                    <td className="px-3 py-2 text-right"><MoneyDisplay amount={r.montantRetenue} size="sm" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2 className="font-semibold mb-3">Calendrier fiscal — prochaines échéances</h2>
        {calendrier.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune échéance dans les 6 prochains mois.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {calendrier.map((e, i) => (
              <li key={`${e.code}-${i}`} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-gray-800">{e.libelle}</p>
                  <p className="text-xs text-gray-500">{e.dateLimite}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statutColor(e.statut)}`}>
                  {e.joursRestants} j — {statutLabel(e.statut)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TvaCard({ title, docs, compta, ecart }:
  { title: string; docs: number; compta: number; ecart: number }) {
  const ok = Math.abs(ecart) <= 0.01;
  return (
    <div className="card">
      <p className="text-xs text-gray-500">{title}</p>
      <div className="mt-1 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Documents</span>
          <MoneyDisplay amount={docs} size="sm" /></div>
        <div className="flex justify-between"><span className="text-gray-500">Comptabilité</span>
          <MoneyDisplay amount={compta} size="sm" /></div>
        <div className={`flex justify-between font-semibold ${ok ? 'text-green-600' : 'text-red-600'}`}>
          <span>Écart</span><MoneyDisplay amount={ecart} size="sm" /></div>
      </div>
    </div>
  );
}

function statutColor(s: EcheanceFiscale['statut']): string {
  return s === 'URGENT' ? 'bg-red-100 text-red-700'
    : s === 'PROCHE' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600';
}
function statutLabel(s: EcheanceFiscale['statut']): string {
  return s === 'URGENT' ? 'Urgent' : s === 'PROCHE' ? 'Proche' : 'À venir';
}
function frMois(m: number): string {
  return ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août',
    'Septembre', 'Octobre', 'Novembre', 'Décembre'][m - 1];
}

export default ControleFiscal;
